import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ebdApi, membrosApi } from '../lib/api';
import { filterRankingBySalaTipo } from '../lib/ebdSalaTipo';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  BottomSheetContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import {
  Plus,
  Minus,
  BookOpen,
  Users,
  Trophy,
  Calendar,
  Save,
  Loader2,
  Presentation,
  Pencil,
  Award,
  Star,
  Medal,
  MoreVertical,
  X,
  Trash2,
  Check,
  Circle,
  Wallet,
} from 'lucide-react';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  LineChart,
  Line,
} from 'recharts';

const PRESENCA_FIELDS = [
  { key: 'presente',       label: 'Presente' },
  { key: 'trouxe_biblia',  label: 'Bíblia' },
  { key: 'trouxe_revista', label: 'Revista' },
  { key: 'licao_estudada', label: 'Estudou' },
];

function VisitanteNumeroControl({ value, onChange }) {
  const atual = Math.max(0, Math.floor(Number(value) || 0));
  return (
    <div className="inline-flex shrink-0 items-center overflow-hidden rounded-xl border border-[#E5E7EB] bg-white shadow-sm">
      <Button
        type="button"
        variant="ghost"
        className="h-11 min-h-[44px] w-11 min-w-[44px] shrink-0 rounded-none text-[#0F172A] hover:bg-[#F1F5F9] active:scale-[0.98]"
        onClick={() => onChange(Math.max(0, atual - 1))}
        aria-label="Diminuir visitantes"
      >
        <Minus className="h-5 w-5" />
      </Button>
      <div className="min-w-[3rem] px-3 text-center text-base font-semibold tabular-nums text-[#0F172A]">
        {atual}
      </div>
      <Button
        type="button"
        variant="ghost"
        className="h-11 min-h-[44px] w-11 min-w-[44px] shrink-0 rounded-none text-[#0F172A] hover:bg-[#F1F5F9] active:scale-[0.98]"
        onClick={() => onChange(atual + 1)}
        aria-label="Aumentar visitantes"
      >
        <Plus className="h-5 w-5" />
      </Button>
    </div>
  );
}

function PresencaCards({ presencas, onUpdate, canGerirAlunos, onOpenExcluirAluno, onOpenEditarAluno }) {
  return (
    <div className="space-y-4">
      {presencas.map((p) => (
        <div
          key={p.aluno_id}
          className="rounded-xl border border-[#E5E7EB] bg-white p-4 shadow-[0_1px_2px_rgba(0,0,0,0.06)]"
        >
          <div className="mb-4 flex items-start justify-between gap-3">
            <p className="min-w-0 flex-1 text-base font-semibold leading-snug text-[#0F172A]">{p.nome}</p>
            {canGerirAlunos && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    className="h-11 w-11 min-h-[44px] min-w-[44px] shrink-0 text-[#64748B] hover:bg-[#F1F5F9]"
                    aria-label="Ações do aluno"
                  >
                    <MoreVertical className="h-5 w-5" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-48">
                  <DropdownMenuItem onSelect={() => onOpenEditarAluno(p.aluno_id, p.nome)}>
                    <Pencil className="mr-2 h-4 w-4" />
                    Editar nome
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="text-red-600 focus:text-red-600"
                    onSelect={() => onOpenExcluirAluno(p.aluno_id, p.nome)}
                  >
                    <Trash2 className="mr-2 h-4 w-4" />
                    Excluir da EBD
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>
          <div className="grid grid-cols-2 gap-3">
            {PRESENCA_FIELDS.map(({ key, label }) => {
              const on = !!p[key];
              return (
                <button
                  key={key}
                  type="button"
                  role="switch"
                  aria-checked={on}
                  aria-label={label}
                  onClick={() => onUpdate(p.aluno_id, key, !on)}
                  className={cn(
                    'flex min-h-[48px] w-full items-center justify-center gap-2 rounded-xl border-2 px-3 py-2.5 text-sm font-medium transition-colors active:scale-[0.99]',
                    on
                      ? 'border-[#2563EB] bg-[#DBEAFE] text-[#0F172A] shadow-sm'
                      : 'border-[#E5E7EB] bg-white text-[#64748B] hover:border-[#CBD5E1] hover:bg-slate-50'
                  )}
                >
                  {on ? (
                    <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-[#2563EB] text-white">
                      <Check className="h-3.5 w-3.5" strokeWidth={3} />
                    </span>
                  ) : (
                    <Circle className="h-6 w-6 shrink-0 text-[#CBD5E1]" strokeWidth={2} />
                  )}
                  {label}
                </button>
              );
            })}
          </div>
          <div className="mt-4 flex min-h-[48px] items-center justify-between gap-3 rounded-xl border border-[#E5E7EB] bg-[#F8FAFC] px-3 py-2">
            <span className="min-w-0 flex-1 truncate pr-2 text-sm font-medium text-[#0F172A]">
              Visitantes trazidos
            </span>
            <VisitanteNumeroControl
              value={p.visitante_numero}
              onChange={(n) => onUpdate(p.aluno_id, 'visitante_numero', n)}
            />
          </div>
        </div>
      ))}
    </div>
  );
}

function AlunoRowAcoes({ alunoId, nome, canGerir, onEditar, onExcluir }) {
  if (!canGerir) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="h-8 w-8 text-slate-600 hover:bg-slate-100"
          aria-label="Ações do aluno"
        >
          <MoreVertical className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuItem onSelect={() => onEditar(alunoId, nome)}>
          <Pencil className="mr-2 h-4 w-4" />
          Editar nome
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          className="text-red-600 focus:text-red-600"
          onSelect={() => onExcluir(alunoId, nome)}
        >
          <Trash2 className="mr-2 h-4 w-4" />
          Excluir da EBD
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

export const EBD = () => {
  const { user } = useAuth();
  const isProfessor = user?.nivel_acesso === 'professor';
  const nivelNorm = String(user?.nivel_acesso || '').toLowerCase();
  /** Superintendente ou super_admin: excluir sala, desativar aluno, editar nome. */
  const canGerirAlunosEbd = nivelNorm === 'superintendente' || nivelNorm === 'super_admin';
  const [salas, setSalas] = useState([]);
  const [alunos, setAlunos] = useState([]);
  const [ranking, setRanking] = useState([]);
  const [membros, setMembros] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('salas');
  const [salaDialogOpen, setSalaDialogOpen] = useState(false);
  const [editSalaDialogOpen, setEditSalaDialogOpen] = useState(false);
  const [chamadaDialogOpen, setChamadaDialogOpen] = useState(false);
  const [alunoDialogOpen, setAlunoDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [pontuacaoDialogOpen, setPontuacaoDialogOpen] = useState(false);
  const [fabOpen, setFabOpen] = useState(false);
  const [excluirAlunoDialog, setExcluirAlunoDialog] = useState({
    open: false,
    alunoId: null,
    nome: '',
  });
  const [excluirSalaDialog, setExcluirSalaDialog] = useState({ open: false, sala: null });
  const [editarAlunoDialog, setEditarAlunoDialog] = useState({
    open: false,
    alunoId: null,
    nome: '',
  });
  const [excluindoAluno, setExcluindoAluno] = useState(false);
  const [excluindoSala, setExcluindoSala] = useState(false);
  const pendingExcluirAlunoRef = useRef({ alunoId: null, nome: '' });
  const pendingExcluirSalaRef = useRef({ sala: null });
  const [selectedSala, setSelectedSala] = useState(null);
  const [periodoRanking, setPeriodoRanking] = useState('mes');
  const [dataRankingDia, setDataRankingDia] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [rankingPublicoTab, setRankingPublicoTab] = useState('infantil');
  const rankingTabs = ['infantil', 'adulto'];
  const swipeTouchX = useRef(null);
  const handleRankingTouchStart = useCallback((e) => {
    swipeTouchX.current = e.touches[0].clientX;
  }, []);
  const handleRankingTouchEnd = useCallback((e) => {
    if (swipeTouchX.current === null) return;
    const delta = e.changedTouches[0].clientX - swipeTouchX.current;
    swipeTouchX.current = null;
    if (Math.abs(delta) < 50) return;
    const currentIdx = rankingTabs.indexOf(rankingPublicoTab);
    if (delta < 0 && currentIdx < rankingTabs.length - 1) {
      setRankingPublicoTab(rankingTabs[currentIdx + 1]);
    } else if (delta > 0 && currentIdx > 0) {
      setRankingPublicoTab(rankingTabs[currentIdx - 1]);
    }
  }, [rankingPublicoTab]);

  const [salaForm, setSalaForm] = useState({ nome: '', faixa_etaria: '', tipo_sala: 'adulto', avatar: null });
  const [salaAvatarPreview, setSalaAvatarPreview] = useState(null);
  const [editSalaForm, setEditSalaForm] = useState({
    id: '',
    nome: '',
    tipo_sala: 'adulto',
    faixa_etaria: '',
    ativo: true,
    avatar: null,
  });
  const [editSalaAvatarPreview, setEditSalaAvatarPreview] = useState(null);
  const [alunoForm, setAlunoForm] = useState({ nome: '', sala_id: '' });
  const [chamadaData, setChamadaData] = useState({
    data_aula: format(new Date(), 'yyyy-MM-dd'),
    licao_estudada: '',
    oferta_sala: '0',
    presencas: [],
  });

  const [ofertaFinPeriodo, setOfertaFinPeriodo] = useState('mes');
  const [ofertaFinDataRef, setOfertaFinDataRef] = useState(() => format(new Date(), 'yyyy-MM-dd'));
  const [ofertaFinLoading, setOfertaFinLoading] = useState(false);
  const [ofertaFinData, setOfertaFinData] = useState(null);

  useEffect(() => {
    fetchData();
  }, [periodoRanking, dataRankingDia]);

  useEffect(() => {
    if (isProfessor || activeTab !== 'financeiro' || !user?.igreja_id) return undefined;
    let cancelled = false;
    (async () => {
      setOfertaFinLoading(true);
      try {
        const res = await ebdApi.getOfertasRelatorio({
          igreja_id: user.igreja_id,
          periodo: ofertaFinPeriodo,
          data_referencia: ofertaFinDataRef,
        });
        if (!cancelled) setOfertaFinData(res.data);
      } catch (err) {
        if (!cancelled) {
          setOfertaFinData(null);
          const d = err.response?.data?.detail;
          toast.error(typeof d === 'string' ? d : 'Não foi possível carregar o relatório de ofertas.');
        }
      } finally {
        if (!cancelled) setOfertaFinLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isProfessor, activeTab, user?.igreja_id, ofertaFinPeriodo, ofertaFinDataRef]);

  const fetchData = async () => {
    try {
      const rankingParams = { igreja_id: user?.igreja_id, periodo: periodoRanking };
      if (periodoRanking === 'dia' && dataRankingDia) {
        rankingParams.data = dataRankingDia;
      }
      const [salasRes, membrosRes, rankingRes] = await Promise.all([
        ebdApi.listSalas(user?.igreja_id),
        membrosApi.list({ igreja_id: user?.igreja_id }),
        ebdApi.getRanking(rankingParams),
      ]);
      setSalas(salasRes.data || []);
      setMembros(membrosRes.data || []);
      setRanking(rankingRes.data || []);
    } catch (error) {
      console.error('Error fetching EBD data:', error);
    } finally {
      setLoading(false);
    }
  };

  const abrirExcluirSala = (sala) => {
    pendingExcluirSalaRef.current = { sala };
    setExcluirSalaDialog({ open: true, sala });
  };

  const confirmarExcluirSala = async () => {
    const sala = pendingExcluirSalaRef.current.sala;
    if (!sala?.id) return;
    setExcluindoSala(true);
    try {
      await ebdApi.deleteSala(sala.id);
      toast.success('Sala excluída.');
      setExcluirSalaDialog({ open: false, sala: null });
      pendingExcluirSalaRef.current = { sala: null };
      await fetchData();
      if (selectedSala?.id === sala.id) {
        setSelectedSala(null);
        setChamadaData((prev) => ({ ...prev, presencas: [] }));
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir sala');
    } finally {
      setExcluindoSala(false);
    }
  };

  const abrirExcluirAluno = (alunoId, nome) => {
    const n = nome || '';
    pendingExcluirAlunoRef.current = { alunoId, nome: n };
    setExcluirAlunoDialog({ open: true, alunoId, nome: n });
  };

  const confirmarExcluirAluno = async () => {
    const alunoId = pendingExcluirAlunoRef.current.alunoId;
    if (!alunoId) return;
    setExcluindoAluno(true);
    try {
      await ebdApi.deleteAluno(alunoId);
      toast.success('Aluno removido da EBD.');
      setExcluirAlunoDialog({ open: false, alunoId: null, nome: '' });
      pendingExcluirAlunoRef.current = { alunoId: null, nome: '' };
      setChamadaData((prev) => ({
        ...prev,
        presencas: prev.presencas.filter((pr) => String(pr.aluno_id) !== String(alunoId)),
      }));
      setAlunos((prev) => prev.filter((a) => String(a.id) !== String(alunoId)));
      await fetchData();
      if (selectedSala) {
        fetchAlunosSala(selectedSala.id, chamadaData.data_aula);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir aluno');
    } finally {
      setExcluindoAluno(false);
    }
  };

  const abrirEditarAluno = (alunoId, nome) => {
    setEditarAlunoDialog({ open: true, alunoId, nome: nome || '' });
  };

  const handleSalvarNomeAluno = async (e) => {
    e.preventDefault();
    const { alunoId, nome } = editarAlunoDialog;
    if (!alunoId) return;
    const trimmed = String(nome || '').trim();
    if (!trimmed) {
      toast.error('Informe o nome do aluno');
      return;
    }
    setSubmitting(true);
    try {
      await ebdApi.updateAluno(alunoId, { nome: trimmed });
      toast.success('Nome atualizado.');
      setEditarAlunoDialog({ open: false, alunoId: null, nome: '' });
      setChamadaData((prev) => ({
        ...prev,
        presencas: prev.presencas.map((pr) =>
          String(pr.aluno_id) === String(alunoId) ? { ...pr, nome: trimmed } : pr
        ),
      }));
      setAlunos((prev) =>
        prev.map((a) => (String(a.id) === String(alunoId) ? { ...a, nome: trimmed } : a))
      );
      await fetchData();
      if (selectedSala) {
        fetchAlunosSala(selectedSala.id, chamadaData.data_aula);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar nome');
    } finally {
      setSubmitting(false);
    }
  };

  const rankingInfantil = useMemo(
    () => filterRankingBySalaTipo(ranking, salas, 'infantil'),
    [ranking, salas]
  );
  const rankingAdulto = useMemo(
    () => filterRankingBySalaTipo(ranking, salas, 'adulto'),
    [ranking, salas]
  );
  const normalizePresencasPayload = (payload) => {
    if (Array.isArray(payload)) return payload;
    if (payload?.items && Array.isArray(payload.items)) return payload.items;
    if (payload?.data && Array.isArray(payload.data)) return payload.data;
    return [];
  };

  const fetchAlunosSala = async (salaId, dataAula) => {
    if (!salaId) return;
    const data = dataAula;
    try {
      const alunosRes = await ebdApi.listAlunos({ sala_id: salaId });
      const alunos = alunosRes.data || [];
      setAlunos(alunos);

      let presencasApi = [];
      let presencasRequestOk = false;
      if (data) {
        try {
          const params = { data_aula: data };
          params.sala_id = salaId;
          if (user?.igreja_id) params.igreja_id = user.igreja_id;
          const presRes = await ebdApi.listPresencas(params);
          presencasApi = normalizePresencasPayload(presRes.data);
          presencasRequestOk = true;
        } catch (err) {
          console.error('Error fetching presencas for date:', err);
        }
      }

      const forSala = presencasApi.filter((p) => {
        if (p.sala_id == null || p.sala_id === undefined) return true;
        return String(p.sala_id) === String(salaId);
      });

      let ofertaSalaStr = '0';
      if (data) {
        const igrejaChamadaId =
          user?.igreja_id || salas.find((s) => String(s.id) === String(salaId))?.igreja_id;
        if (igrejaChamadaId) {
          try {
            const ofRes = await ebdApi.getSalaOfertaDia({
              sala_id: salaId,
              data_aula: data,
              igreja_id: igrejaChamadaId,
            });
            const v = ofRes.data?.valor;
            ofertaSalaStr = v != null && !Number.isNaN(Number(v)) ? String(v) : '0';
          } catch (errOf) {
            console.error('Error fetching oferta da sala:', errOf);
          }
        }
      }

      const byAluno = new Map();
      let licaoFromApi = '';
      for (const pr of forSala) {
        const aid = pr.aluno_id ?? pr.aluno?.id;
        if (aid != null) byAluno.set(aid, pr);
        if (pr.licao_estudada && String(pr.licao_estudada).trim() && !licaoFromApi) {
          licaoFromApi = String(pr.licao_estudada).trim();
        }
      }

      setChamadaData((prev) => ({
        ...prev,
        ...(data ? { data_aula: data } : {}),
        oferta_sala: ofertaSalaStr,
        presencas: alunos.map((a) => {
          const ex = byAluno.get(a.id);
          const visitanteNumero =
            ex?.visitante_numero != null && !Number.isNaN(Number(ex.visitante_numero))
              ? Math.max(0, Math.floor(Number(ex.visitante_numero)))
              : ex?.trouxe_visitante
                ? 1
                : 0;
          return {
            aluno_id: a.id,
            nome: a.nome,
            presente: !!ex?.presente,
            trouxe_biblia: !!ex?.trouxe_biblia,
            trouxe_revista: !!ex?.trouxe_revista,
            visitante_numero: visitanteNumero,
            licao_estudada: !!ex?.licao_estudada,
          };
        }),
        licao_estudada:
          licaoFromApi ||
          (data && presencasRequestOk && forSala.length === 0 ? '' : prev.licao_estudada),
      }));
    } catch (error) {
      console.error('Error fetching alunos:', error);
    }
  };

  const handleCreateSala = async (e) => {
    e.preventDefault();
    if (!salaForm.nome) {
      toast.error('Nome da sala é obrigatório');
      return;
    }

    setSubmitting(true);
    try {
      await ebdApi.createSala({
        ...salaForm,
        igreja_id: user?.igreja_id,
      });
      toast.success('Sala criada com sucesso!');
      setSalaDialogOpen(false);
      setSalaForm({ nome: '', faixa_etaria: '', tipo_sala: 'adulto', avatar: null });
      setSalaAvatarPreview(null);
      fetchData();
    } catch (error) {
      toast.error('Erro ao criar sala');
    } finally {
      setSubmitting(false);
    }
  };

  const openEditSala = (sala) => {
    const tipo = String(sala.tipo_sala || 'adulto').toLowerCase();
    setEditSalaForm({
      id: sala.id,
      nome: sala.nome || '',
      tipo_sala: tipo === 'infantil' ? 'infantil' : 'adulto',
      faixa_etaria: sala.faixa_etaria || '',
      ativo: sala.ativo !== false,
      avatar: null,
    });
    setEditSalaAvatarPreview(sala.avatar_url || null);
    setEditSalaDialogOpen(true);
  };

  const handleUpdateSala = async (e) => {
    e.preventDefault();
    if (!editSalaForm.nome?.trim()) {
      toast.error('Nome da sala é obrigatório');
      return;
    }
    setSubmitting(true);
    try {
      await ebdApi.updateSala(editSalaForm.id, {
        nome: editSalaForm.nome.trim(),
        tipo_sala: editSalaForm.tipo_sala,
        faixa_etaria: editSalaForm.faixa_etaria || '',
        ativo: !!editSalaForm.ativo,
        ...(editSalaForm.avatar ? { avatar: editSalaForm.avatar } : {}),
      });
      toast.success('Sala atualizada!');
      setEditSalaDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao atualizar sala');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCreateAluno = async (e) => {
    e.preventDefault();
    if (!alunoForm.sala_id) {
      toast.error('Selecione uma sala');
      return;
    }
    if (!alunoForm.nome.trim()) {
      toast.error('Informe o nome do aluno');
      return;
    }

    const salaSelecionada = salas.find((s) => s.id === alunoForm.sala_id);
    const igrejaId = user?.igreja_id || salaSelecionada?.igreja_id;
    if (!igrejaId) {
      toast.error('Não foi possível identificar a igreja. Selecione outra sala ou vincule o usuário a uma igreja.');
      setSubmitting(false);
      return;
    }

    setSubmitting(true);
    try {
      await ebdApi.createAluno({
        ...alunoForm,
        igreja_id: igrejaId,
      });
      toast.success('Aluno adicionado com sucesso!');
      setAlunoDialogOpen(false);
      setAlunoForm({ nome: '', sala_id: '' });
      if (selectedSala) {
        fetchAlunosSala(selectedSala.id, chamadaData.data_aula);
      }
    } catch (error) {
      toast.error('Erro ao adicionar aluno');
    } finally {
      setSubmitting(false);
    }
  };

  const handleSalvarChamada = async () => {
    if (!selectedSala) {
      toast.error('Selecione uma sala');
      return;
    }

    const igrejaChamada = user?.igreja_id || selectedSala?.igreja_id;
    if (!igrejaChamada) {
      toast.error('Não foi possível identificar a igreja para a chamada.');
      return;
    }

    setSubmitting(true);
    try {
      for (const presenca of chamadaData.presencas) {
        const visitanteNumero = Math.max(0, Math.floor(Number(presenca.visitante_numero) || 0));
        await ebdApi.createPresenca({
          aluno_id: presenca.aluno_id,
          sala_id: selectedSala.id,
          data_aula: chamadaData.data_aula,
          presente: presenca.presente,
          trouxe_biblia: presenca.trouxe_biblia,
          trouxe_revista: presenca.trouxe_revista,
          visitante_numero: visitanteNumero,
          trouxe_visitante: visitanteNumero > 0,
          licao_estudada: presenca.licao_estudada,
          oferta: 0,
          igreja_id: igrejaChamada,
        });
      }
      const valorOfertaSala = parseFloat(String(chamadaData.oferta_sala || '').replace(',', '.')) || 0;
      await ebdApi.putSalaOfertaDia({
        sala_id: selectedSala.id,
        data_aula: chamadaData.data_aula,
        igreja_id: igrejaChamada,
        valor: valorOfertaSala,
      });
      toast.success('Chamada salva com sucesso!');
      setChamadaDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error('Erro ao salvar chamada');
    } finally {
      setSubmitting(false);
    }
  };

  const updatePresenca = (alunoId, field, value) => {
    setChamadaData((prev) => ({
      ...prev,
      presencas: prev.presencas.map((p) =>
        p.aluno_id === alunoId ? { ...p, [field]: value } : p
      ),
    }));
  };

  const openApresentacaoNovaAba = () => {
    const params = new URLSearchParams({ periodo: periodoRanking, data: dataRankingDia });
    const path = `${window.location.origin}/ebd/apresentacao?${params.toString()}`;
    window.open(path, '_blank', 'noopener,noreferrer');
  };

  const getRankingIcon = (posicao) => {
    switch (posicao) {
      case 1:
        return <Trophy className="w-6 h-6 text-amber-500" />;
      case 2:
        return <Medal className="w-6 h-6 text-slate-400" />;
      case 3:
        return <Award className="w-6 h-6 text-amber-700" />;
      default:
        return <Star className="w-5 h-5 text-slate-300" />;
    }
  };

  return (
    <div className="ebd-scope space-y-6 animate-fade-in" data-testid="ebd-page">
      {/* Header */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="page-title">Escola Bíblica Dominical</h1>
          <p className="page-subtitle">Gerencie as classes e aulas da EBD</p>
        </div>
        <div className="hidden flex-wrap items-center gap-3 sm:flex">
          <Dialog open={salaDialogOpen} onOpenChange={setSalaDialogOpen}>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                data-testid="add-sala-btn"
                className="h-10 rounded-lg border-[#E5E7EB] bg-white px-4 text-sm font-medium text-[#0F172A] hover:bg-slate-50 active:scale-[0.98]"
              >
                <Plus className="mr-2 h-4 w-4" />
                Nova sala
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Nova Sala</DialogTitle>
                <DialogDescription>Crie uma nova sala/classe de EBD</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateSala} className="space-y-4">
                <div className="flex flex-col items-center gap-2">
                  <div className="h-20 w-20 rounded-full border-2 border-dashed border-slate-200 overflow-hidden flex items-center justify-center bg-slate-50 cursor-pointer"
                    onClick={() => document.getElementById('avatar_sala').click()}>
                    {salaAvatarPreview
                      ? <img src={salaAvatarPreview} alt="avatar" className="h-full w-full object-cover" />
                      : <BookOpen className="h-8 w-8 text-slate-300" />}
                  </div>
                  <input
                    id="avatar_sala"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setSalaForm((p) => ({ ...p, avatar: file }));
                      setSalaAvatarPreview(URL.createObjectURL(file));
                    }}
                  />
                  <Label htmlFor="avatar_sala" className="cursor-pointer text-xs font-medium text-[#2563EB] hover:underline">
                    {salaAvatarPreview ? 'Trocar foto' : 'Adicionar foto'}
                  </Label>
                </div>
                <div>
                  <Label htmlFor="nome_sala">Nome da Sala *</Label>
                  <Input
                    id="nome_sala"
                    value={salaForm.nome}
                    onChange={(e) => setSalaForm({ ...salaForm, nome: e.target.value })}
                    className="input-field mt-1"
                    placeholder="Ex: Jovens, Adultos, Crianças"
                    data-testid="input-nome-sala"
                  />
                </div>
                <div>
                  <Label htmlFor="tipo_sala">Público *</Label>
                  <Select
                    value={salaForm.tipo_sala}
                    onValueChange={(v) => setSalaForm({ ...salaForm, tipo_sala: v })}
                  >
                    <SelectTrigger id="tipo_sala" className="input-field mt-1" data-testid="select-tipo-sala">
                      <SelectValue placeholder="Selecione" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="adulto">Adulto</SelectItem>
                      <SelectItem value="infantil">Infantil</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="faixa_etaria">Faixa Etária</Label>
                  <Input
                    id="faixa_etaria"
                    value={salaForm.faixa_etaria}
                    onChange={(e) => setSalaForm({ ...salaForm, faixa_etaria: e.target.value })}
                    className="input-field mt-1"
                    placeholder="Ex: 18-30 anos"
                    data-testid="input-faixa-etaria"
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setSalaDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit" className="ebd-btn-primary" disabled={submitting} data-testid="submit-sala">
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Criar Sala'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={editSalaDialogOpen} onOpenChange={setEditSalaDialogOpen}>
            <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-md">
              <DialogHeader>
                <DialogTitle>Editar sala</DialogTitle>
                <DialogDescription>Altere os dados da sala e salve.</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleUpdateSala} className="space-y-4">
                <div className="flex flex-col items-center gap-2">
                  <div className="h-20 w-20 rounded-full border-2 border-dashed border-slate-200 overflow-hidden flex items-center justify-center bg-slate-50 cursor-pointer"
                    onClick={() => document.getElementById('edit_avatar_sala').click()}>
                    {editSalaAvatarPreview
                      ? <img src={editSalaAvatarPreview} alt="avatar" className="h-full w-full object-cover" />
                      : <BookOpen className="h-8 w-8 text-slate-300" />}
                  </div>
                  <input
                    id="edit_avatar_sala"
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={(e) => {
                      const file = e.target.files?.[0];
                      if (!file) return;
                      setEditSalaForm((p) => ({ ...p, avatar: file }));
                      setEditSalaAvatarPreview(URL.createObjectURL(file));
                    }}
                  />
                  <Label htmlFor="edit_avatar_sala" className="cursor-pointer text-xs font-medium text-[#2563EB] hover:underline">
                    {editSalaAvatarPreview ? 'Trocar foto' : 'Adicionar foto'}
                  </Label>
                </div>
                <div>
                  <Label htmlFor="edit_nome_sala">Nome da sala *</Label>
                  <Input
                    id="edit_nome_sala"
                    value={editSalaForm.nome}
                    onChange={(e) => setEditSalaForm({ ...editSalaForm, nome: e.target.value })}
                    className="input-field mt-1"
                    data-testid="edit-input-nome-sala"
                  />
                </div>
                <div>
                  <Label htmlFor="edit_tipo_sala">Público *</Label>
                  <Select
                    value={editSalaForm.tipo_sala}
                    onValueChange={(v) => setEditSalaForm({ ...editSalaForm, tipo_sala: v })}
                  >
                    <SelectTrigger id="edit_tipo_sala" className="input-field mt-1" data-testid="edit-select-tipo-sala">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="adulto">Adulto</SelectItem>
                      <SelectItem value="infantil">Infantil</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label htmlFor="edit_faixa_etaria">Faixa etária</Label>
                  <Input
                    id="edit_faixa_etaria"
                    value={editSalaForm.faixa_etaria}
                    onChange={(e) => setEditSalaForm({ ...editSalaForm, faixa_etaria: e.target.value })}
                    className="input-field mt-1"
                    placeholder="Ex.: 18–30 anos"
                    data-testid="edit-input-faixa-etaria"
                  />
                </div>
                <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2">
                  <Label htmlFor="edit_sala_ativo" className="cursor-pointer">
                    Sala ativa
                  </Label>
                  <Switch
                    id="edit_sala_ativo"
                    checked={editSalaForm.ativo}
                    onCheckedChange={(checked) => setEditSalaForm({ ...editSalaForm, ativo: checked })}
                    data-testid="edit-switch-sala-ativo"
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setEditSalaDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit" className="ebd-btn-primary" disabled={submitting} data-testid="submit-edit-sala">
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Salvar'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>

          <Dialog open={alunoDialogOpen} onOpenChange={setAlunoDialogOpen}>
            <DialogTrigger asChild>
              <Button
                variant="outline"
                data-testid="add-aluno-btn"
                className="h-10 rounded-lg border-[#E5E7EB] bg-white px-4 text-sm font-medium text-[#0F172A] hover:bg-slate-50 active:scale-[0.98]"
              >
                <Users className="mr-2 h-4 w-4" />
                Adicionar aluno
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>Adicionar Aluno</DialogTitle>
                <DialogDescription>Adicione um membro ou visitante à EBD</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateAluno} className="space-y-4">
                <div>
                  <Label>Sala *</Label>
                  <Select
                    value={alunoForm.sala_id}
                    onValueChange={(v) => setAlunoForm({ ...alunoForm, sala_id: v })}
                  >
                    <SelectTrigger className="input-field mt-1" data-testid="select-sala-aluno">
                      <SelectValue placeholder="Selecione a sala" />
                    </SelectTrigger>
                    <SelectContent>
                      {salas.map((sala) => (
                        <SelectItem key={sala.id} value={sala.id}>{sala.nome}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Nome do Aluno *</Label>
                  <Input
                    value={alunoForm.nome}
                    onChange={(e) => setAlunoForm({ ...alunoForm, nome: e.target.value })}
                    className="input-field mt-1"
                    placeholder="Nome completo do aluno"
                    data-testid="input-nome-aluno"
                  />
                </div>
                <DialogFooter>
                  <Button type="button" variant="outline" onClick={() => setAlunoDialogOpen(false)}>
                    Cancelar
                  </Button>
                  <Button type="submit" className="ebd-btn-primary" disabled={submitting} data-testid="submit-aluno">
                    {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Adicionar'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-4 md:grid-cols-3 md:gap-4">
        <Card className="ebd-panel-card">
          <CardContent className="flex min-h-[100px] items-center gap-4 p-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#DBEAFE]">
              <BookOpen className="h-5 w-5 text-[#2563EB]" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-2xl font-bold leading-none text-[#0F172A]">{salas.length}</p>
              <p className="mt-1 text-sm text-[#64748B]">Total de salas</p>
            </div>
          </CardContent>
        </Card>

        <Card className="ebd-panel-card">
          <CardContent className="flex min-h-[100px] items-center gap-4 p-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#DCFCE7]">
              <Users className="h-5 w-5 text-[#16A34A]" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-2xl font-bold leading-none text-[#0F172A]">
                {salas.reduce((acc, s) => acc + (s.total_alunos || 0), 0)}
              </p>
              <p className="mt-1 text-sm text-[#64748B]">Total de alunos</p>
            </div>
          </CardContent>
        </Card>

        <Card className="ebd-panel-card">
          <CardContent className="flex min-h-[100px] items-start gap-4 p-4">
            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-[#FEF3C7]">
              <Trophy className="h-5 w-5 text-[#F59E0B]" />
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-sm text-[#64748B]">Líderes do ranking</p>
              <div className={`mt-1 space-y-1 ${isProfessor ? 'blur-sm select-none pointer-events-none' : ''}`}>
                <p className="text-xs font-medium text-[#64748B]">Infantil</p>
                <p className="truncate text-base font-bold text-[#0F172A]">
                  {rankingInfantil[0]?.sala_nome || '—'}
                </p>
                <p className="pt-1 text-xs font-medium text-[#64748B]">Adulto</p>
                <p className="truncate text-base font-bold text-[#0F172A]">
                  {rankingAdulto[0]?.sala_nome || '—'}
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="mt-2 h-auto px-0 text-xs font-medium text-[#2563EB] hover:bg-transparent hover:text-[#1D4ED8]"
                onClick={() => setPontuacaoDialogOpen(true)}
              >
                Sistema de pontuação
              </Button>
            </div>
          </CardContent>
        </Card>

        <Dialog open={pontuacaoDialogOpen} onOpenChange={setPontuacaoDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Trophy className="h-5 w-5 text-amber-500" />
                Sistema de Pontuação
              </DialogTitle>
              <DialogDescription>
                Pontos acumulados por aluno a cada aula registrada.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3 py-2">
              {[
                { pts: '+10', label: 'Presença', desc: 'Aluno presente na aula', color: 'bg-sky-100 text-sky-700' },
                { pts: '+5',  label: 'Bíblia',   desc: 'Trouxe a bíblia',         color: 'bg-emerald-100 text-emerald-700' },
                { pts: '+5',  label: 'Revista',   desc: 'Trouxe a revista',        color: 'bg-violet-100 text-violet-700' },
                { pts: '+5',  label: 'Estudou',   desc: 'Estudou a lição',         color: 'bg-teal-100 text-teal-700' },
                { pts: '+15', label: 'Visitante', desc: 'Por visitante trazido',     color: 'bg-amber-100 text-amber-700' },
                { pts: '+1',  label: 'Oferta',    desc: 'Por cada R$ 1,00 na oferta total da sala (por aula)', color: 'bg-pink-100 text-pink-700' },
              ].map(({ pts, label, desc, color }) => (
                <div key={label} className="flex items-center gap-3 rounded-lg border border-slate-100 px-3 py-2">
                  <Badge className={`shrink-0 ${color}`}>{pts}</Badge>
                  <div>
                    <p className="text-sm font-medium text-slate-900">{label}</p>
                    <p className="text-xs text-slate-500">{desc}</p>
                  </div>
                </div>
              ))}
            </div>
          </DialogContent>
        </Dialog>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="inline-flex h-auto min-h-0 flex-wrap gap-1 rounded-lg border border-[#E5E7EB] bg-[#F8FAFC] p-1">
          <TabsTrigger
            value="salas"
            className="h-11 gap-1.5 rounded-lg px-4 text-sm font-medium text-[#64748B] data-[state=active]:bg-[#DBEAFE] data-[state=active]:text-[#2563EB] data-[state=active]:shadow-none data-[state=inactive]:hover:text-[#0F172A]"
          >
            <BookOpen className="h-4 w-4" />
            <span className="hidden sm:inline">Salas</span>
            <span className="sm:hidden">Salas</span>
          </TabsTrigger>
          <TabsTrigger
            value="chamada"
            className="h-11 gap-1.5 rounded-lg px-4 text-sm font-medium text-[#64748B] data-[state=active]:bg-[#DBEAFE] data-[state=active]:text-[#2563EB] data-[state=active]:shadow-none data-[state=inactive]:hover:text-[#0F172A]"
          >
            <Calendar className="h-4 w-4" />
            <span className="hidden sm:inline">Lançar Chamada</span>
            <span className="sm:hidden">Chamada</span>
          </TabsTrigger>
          {!isProfessor && (
            <>
              <TabsTrigger
                value="ranking"
                className="h-11 gap-1.5 rounded-lg px-4 text-sm font-medium text-[#64748B] data-[state=active]:bg-[#DBEAFE] data-[state=active]:text-[#2563EB] data-[state=active]:shadow-none data-[state=inactive]:hover:text-[#0F172A]"
              >
                <Trophy className="h-4 w-4" />
                Ranking
              </TabsTrigger>
              <TabsTrigger
                value="financeiro"
                className="h-11 gap-1.5 rounded-lg px-4 text-sm font-medium text-[#64748B] data-[state=active]:bg-[#DBEAFE] data-[state=active]:text-[#2563EB] data-[state=active]:shadow-none data-[state=inactive]:hover:text-[#0F172A]"
                data-testid="ebd-tab-financeiro"
              >
                <Wallet className="h-4 w-4" />
                <span className="hidden sm:inline">Financeiro</span>
                <span className="sm:hidden">Financ.</span>
              </TabsTrigger>
            </>
          )}
        </TabsList>

        {/* Salas Tab */}
        <TabsContent value="salas" className="mt-6 outline-none">
          <div className="grid grid-cols-1 gap-4 md:grid-cols-2 lg:grid-cols-3 lg:gap-4">
            {salas.length === 0 ? (
              <Card className="ebd-panel-card col-span-full">
                <CardContent className="py-12 text-center">
                  <BookOpen className="mx-auto h-12 w-12 text-[#94A3B8]" />
                  <h3 className="mt-4 text-lg font-semibold text-[#0F172A]">Nenhuma sala cadastrada</h3>
                  <p className="mt-2 text-sm text-[#64748B]">Crie uma nova sala para começar.</p>
                </CardContent>
              </Card>
            ) : (
              salas.map((sala) => (
                <Card key={sala.id} className="ebd-panel-card" data-testid={`sala-card-${sala.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex min-w-0 flex-1 items-start gap-3">
                        {sala.avatar_url && (
                          <img
                            src={sala.avatar_url}
                            alt={sala.nome}
                            className="h-10 w-10 shrink-0 rounded-full object-cover ring-1 ring-[#E5E7EB]"
                          />
                        )}
                        <h3 className="min-w-0 flex-1 text-base font-semibold leading-snug text-[#0F172A]">
                          {sala.nome}
                        </h3>
                      </div>
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button
                            type="button"
                            variant="ghost"
                            size="icon"
                            className="h-8 w-8 shrink-0 text-[#64748B] hover:bg-[#F1F5F9]"
                            aria-label="Ações da sala"
                          >
                            <MoreVertical className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="min-w-[11rem]">
                          {!isProfessor && (
                            <DropdownMenuItem onClick={() => openEditSala(sala)}>
                              <Pencil className="mr-2 h-4 w-4" />
                              Editar
                            </DropdownMenuItem>
                          )}
                          <DropdownMenuItem
                            onClick={() => {
                              setSelectedSala(sala);
                              fetchAlunosSala(sala.id, chamadaData.data_aula);
                              setChamadaDialogOpen(true);
                            }}
                          >
                            <Calendar className="mr-2 h-4 w-4" />
                            Fazer chamada
                          </DropdownMenuItem>
                          {canGerirAlunosEbd && (
                            <>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem
                                className="text-[#DC2626] focus:text-[#DC2626]"
                                onClick={() => abrirExcluirSala(sala)}
                              >
                                <Trash2 className="mr-2 h-4 w-4" />
                                Excluir sala
                              </DropdownMenuItem>
                            </>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </div>

                    <div className="mt-3 flex flex-wrap gap-2">
                      {sala.tipo_sala && (
                        <span
                          className={
                            String(sala.tipo_sala).toLowerCase() === 'infantil'
                              ? 'inline-flex rounded-full bg-[#DBEAFE] px-2 py-1 text-xs font-medium text-[#2563EB]'
                              : 'inline-flex rounded-full bg-[#F1F5F9] px-2 py-1 text-xs font-medium text-[#0F172A]'
                          }
                        >
                          {String(sala.tipo_sala).toLowerCase() === 'infantil' ? 'Infantil' : 'Adulto'}
                        </span>
                      )}
                      {sala.ativo === false && (
                        <span className="inline-flex rounded-full bg-[#FEF3C7] px-2 py-1 text-xs font-medium text-[#B45309]">
                          Inativa
                        </span>
                      )}
                    </div>

                    <p className="mt-3 text-sm text-[#64748B]">
                      <span className="font-medium text-[#0F172A]">{sala.total_alunos || 0}</span> alunos
                      {sala.faixa_etaria ? (
                        <span className="text-[#94A3B8]"> · {sala.faixa_etaria}</span>
                      ) : null}
                    </p>

                    <div className="mt-4 flex flex-col gap-3">
                      <Button
                        type="button"
                        className="h-9 w-full rounded-lg border-0 bg-[#2563EB] text-sm font-medium text-white shadow-sm hover:bg-[#1D4ED8] active:scale-[0.98]"
                        onClick={() => {
                          setSelectedSala(sala);
                          fetchAlunosSala(sala.id, chamadaData.data_aula);
                          setChamadaDialogOpen(true);
                        }}
                        data-testid={`btn-chamada-${sala.id}`}
                      >
                        <Calendar className="mr-2 h-4 w-4" />
                        Fazer chamada
                      </Button>
                      {!isProfessor && (
                        <Button
                          type="button"
                          variant="outline"
                          className="h-9 w-full rounded-lg border-[#E5E7EB] bg-white text-sm font-medium text-[#0F172A] hover:bg-slate-50 active:scale-[0.98]"
                          onClick={() => openEditSala(sala)}
                          data-testid={`btn-editar-sala-${sala.id}`}
                        >
                          <Pencil className="mr-2 h-4 w-4" />
                          Editar
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        </TabsContent>

        {/* Chamada Tab */}
        <TabsContent value="chamada" className="mt-6 outline-none">
          <Card className="ebd-panel-card">
            <CardHeader>
              <CardTitle className="card-title">Lançar Chamada</CardTitle>
              <CardDescription>Selecione uma sala e registre a presença dos alunos</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Sala</Label>
                  <Select
                    value={selectedSala?.id || ''}
                    onValueChange={(v) => {
                      const sala = salas.find((s) => s.id === v);
                      setSelectedSala(sala);
                      if (sala) fetchAlunosSala(sala.id, chamadaData.data_aula);
                    }}
                  >
                    <SelectTrigger
                      className="input-field mt-1 h-11 min-h-[44px] rounded-xl text-base md:h-10 md:min-h-0 md:text-sm"
                      data-testid="select-sala-chamada"
                    >
                      <SelectValue placeholder="Selecione a sala" />
                    </SelectTrigger>
                    <SelectContent>
                      {salas.map((sala) => (
                        <SelectItem key={sala.id} value={sala.id}>{sala.nome}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Data da Aula</Label>
                  <Input
                    type="date"
                    value={chamadaData.data_aula}
                    onChange={(e) => {
                      const d = e.target.value;
                      setChamadaData((prev) => ({ ...prev, data_aula: d }));
                      if (selectedSala) fetchAlunosSala(selectedSala.id, d);
                    }}
                    className="input-field mt-1 h-11 min-h-[44px] rounded-xl text-base md:h-10 md:min-h-0 md:text-sm"
                    data-testid="input-data-aula"
                  />
                </div>
                <div>
                  <Label>Lição Estudada</Label>
                  <Input
                    value={chamadaData.licao_estudada}
                    onChange={(e) => setChamadaData({ ...chamadaData, licao_estudada: e.target.value })}
                    className="input-field mt-1 h-11 min-h-[44px] rounded-xl text-base md:h-10 md:min-h-0 md:text-sm"
                    placeholder="Ex: Lição 1 - O Amor de Deus"
                    data-testid="input-licao"
                  />
                </div>
              </div>

              {selectedSala && (
                <div className="mt-2 rounded-xl border border-[#E5E7EB] bg-[#F8FAFC] p-4 shadow-sm">
                  <Label htmlFor="oferta-sala-chamada-tab">Oferta total da sala (R$)</Label>
                  <Input
                    id="oferta-sala-chamada-tab"
                    type="number"
                    step="0.01"
                    min="0"
                    className="input-field mt-1 max-w-full rounded-xl text-base md:h-10 md:max-w-xs md:min-h-0 md:text-sm h-11 min-h-[44px]"
                    value={chamadaData.oferta_sala}
                    onChange={(e) => setChamadaData({ ...chamadaData, oferta_sala: e.target.value })}
                    placeholder="0"
                    data-testid="input-oferta-sala-chamada"
                  />
                  <p className="text-xs text-slate-500 mt-2">
                    Valor único para esta sala nesta data. Conta no ranking (+1 ponto por R$ 1,00 no total do dia).
                  </p>
                </div>
              )}

              {selectedSala && chamadaData.presencas.length > 0 && (
                <>
                  <div className="md:hidden mt-4">
                    <PresencaCards
                      presencas={chamadaData.presencas}
                      onUpdate={updatePresenca}
                      canGerirAlunos={canGerirAlunosEbd}
                      onOpenExcluirAluno={abrirExcluirAluno}
                      onOpenEditarAluno={abrirEditarAluno}
                    />
                  </div>
                  <div className="hidden md:block overflow-x-auto mt-6">
                    <Table>
                      <TableHeader>
                        <TableRow className="table-header">
                          <TableHead>Aluno</TableHead>
                          <TableHead className="text-center">Presente</TableHead>
                          <TableHead className="text-center">Bíblia</TableHead>
                          <TableHead className="text-center">Revista</TableHead>
                          <TableHead className="text-center">Estudou</TableHead>
                          <TableHead className="text-center">Visitantes</TableHead>
                          {canGerirAlunosEbd && (
                            <TableHead className="w-14 text-center text-slate-500 font-normal">Ações</TableHead>
                          )}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {chamadaData.presencas.map((p) => (
                          <TableRow key={p.aluno_id}>
                            <TableCell className="font-medium">{p.nome}</TableCell>
                            <TableCell className="text-center">
                              <Checkbox
                                checked={p.presente}
                                onCheckedChange={(c) => updatePresenca(p.aluno_id, 'presente', c)}
                              />
                            </TableCell>
                            <TableCell className="text-center">
                              <Checkbox
                                checked={p.trouxe_biblia}
                                onCheckedChange={(c) => updatePresenca(p.aluno_id, 'trouxe_biblia', c)}
                              />
                            </TableCell>
                            <TableCell className="text-center">
                              <Checkbox
                                checked={p.trouxe_revista}
                                onCheckedChange={(c) => updatePresenca(p.aluno_id, 'trouxe_revista', c)}
                              />
                            </TableCell>
                            <TableCell className="text-center">
                              <Checkbox
                                checked={p.licao_estudada}
                                onCheckedChange={(c) => updatePresenca(p.aluno_id, 'licao_estudada', c)}
                              />
                            </TableCell>
                            <TableCell className="text-center">
                              <div className="flex justify-center">
                                <VisitanteNumeroControl
                                  value={p.visitante_numero}
                                  onChange={(n) => updatePresenca(p.aluno_id, 'visitante_numero', n)}
                                />
                              </div>
                            </TableCell>
                            {canGerirAlunosEbd && (
                              <TableCell className="text-center">
                                <div className="flex justify-center">
                                  <AlunoRowAcoes
                                    alunoId={p.aluno_id}
                                    nome={p.nome}
                                    canGerir={canGerirAlunosEbd}
                                    onEditar={abrirEditarAluno}
                                    onExcluir={abrirExcluirAluno}
                                  />
                                </div>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}

              {selectedSala && chamadaData.presencas.length === 0 && (
                <div className="text-center py-8 text-slate-500">
                  <Users className="mx-auto h-12 w-12 text-slate-300 mb-2" />
                  <p>Nenhum aluno cadastrado nesta sala.</p>
                  <p className="text-sm">Adicione alunos primeiro. Você ainda pode salvar a oferta da sala.</p>
                </div>
              )}

              {selectedSala && (
                <div className="flex justify-stretch pt-2 md:justify-end">
                  <Button
                    onClick={handleSalvarChamada}
                    className="ebd-btn-primary h-12 w-full rounded-xl text-base font-semibold md:h-10 md:w-auto md:text-sm"
                    disabled={submitting}
                    data-testid="salvar-chamada-btn"
                  >
                    {submitting ? (
                      <Loader2 className="mr-2 h-5 w-5 animate-spin md:h-4 md:w-4" />
                    ) : (
                      <Save className="mr-2 h-5 w-5 md:h-4 md:w-4" />
                    )}
                    Salvar chamada
                  </Button>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Ranking Tab */}
        <TabsContent value="ranking" className="mt-6 outline-none">
          <Card className="ebd-panel-card">
            <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
              <div>
                <CardTitle className="card-title flex items-center gap-2">
                  <Trophy className="w-5 h-5 text-amber-500" />
                  Ranking das Salas
                </CardTitle>
                <CardDescription>Pontuação baseada em presença, participação e ofertas</CardDescription>
              </div>
              <div className="flex flex-col gap-3 sm:flex-row sm:items-end">
                {periodoRanking === 'dia' && (
                  <div className="sm:w-44">
                    <Label className="text-xs text-slate-500">Data</Label>
                    <Input
                      type="date"
                      value={dataRankingDia}
                      onChange={(e) => setDataRankingDia(e.target.value)}
                      className="input-field mt-1"
                      data-testid="input-data-ranking-dia"
                    />
                  </div>
                )}
                <Select value={periodoRanking} onValueChange={setPeriodoRanking}>
                  <SelectTrigger className="w-full sm:w-40" data-testid="select-periodo-ranking">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="dia">Dia</SelectItem>
                    <SelectItem value="mes">Este Mês</SelectItem>
                    <SelectItem value="trimestre">Este Trimestre</SelectItem>
                    <SelectItem value="ano">Este Ano</SelectItem>
                  </SelectContent>
                </Select>
                <Button
                  type="button"
                  variant="secondary"
                  className="w-full sm:w-auto shrink-0"
                  onClick={openApresentacaoNovaAba}
                  data-testid="ebd-open-apresentacao-btn"
                >
                  <Presentation className="mr-2 h-4 w-4" />
                  Apresentação (nova aba)
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <Tabs value={rankingPublicoTab} onValueChange={setRankingPublicoTab} className="w-full">
                <TabsList className="mb-6 grid w-full grid-cols-2 bg-slate-100">
                  <TabsTrigger value="infantil" data-testid="ranking-tab-infantil">
                    Infantil
                  </TabsTrigger>
                  <TabsTrigger value="adulto" data-testid="ranking-tab-adulto">
                    Adulto
                  </TabsTrigger>
                </TabsList>
                <div
                  onTouchStart={handleRankingTouchStart}
                  onTouchEnd={handleRankingTouchEnd}
                >
                <TabsContent value="infantil" className="mt-0">
                  {rankingInfantil.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <Trophy className="mx-auto h-12 w-12 text-slate-300 mb-2" />
                      <p>Nenhuma sala infantil no ranking deste período.</p>
                      <p className="text-sm">Cadastre salas como infantil ou registre chamadas.</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {rankingInfantil.map((item, index) => {
                        const maxTaxa = rankingInfantil[0]?.taxa_comparecimento || 1;
                        const taxa = Number(item.taxa_comparecimento ?? 0);
                        const percentual = maxTaxa > 0 ? (taxa / maxTaxa) * 100 : 0;
                        return (
                          <div
                            key={item.sala_id}
                            className={`p-3 sm:p-4 rounded-xl border ${
                              index === 0 ? 'bg-amber-50 border-amber-200' :
                              index === 1 ? 'bg-slate-50 border-slate-200' :
                              index === 2 ? 'bg-orange-50 border-orange-200' :
                              'bg-white border-slate-100'
                            }`}
                            data-testid={`ranking-item-infantil-${item.sala_id}`}
                          >
                            <div className="flex items-center gap-2 sm:gap-4">
                              <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 shrink-0">
                                {getRankingIcon(item.posicao)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex flex-wrap items-start justify-between gap-x-2 gap-y-1 mb-2">
                                  <div className="min-w-0">
                                    <span className="font-semibold text-slate-900 text-sm sm:text-base break-words">{item.sala_nome}</span>
                                    {item.faixa_etaria && (
                                      <span className="text-xs sm:text-sm text-slate-500 ml-1 sm:ml-2">({item.faixa_etaria})</span>
                                    )}
                                  </div>
                                  <div className="text-right shrink-0">
                                    <span className="text-lg sm:text-2xl font-bold text-slate-900 font-heading">
                                      {Math.round(taxa * 100)}
                                    </span>
                                    <span className="text-xs sm:text-sm text-slate-500 ml-1">% pres.</span>
                                  </div>
                                </div>
                                <Progress value={percentual} className="h-2" />
                                <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
                                  <p className="text-xs text-slate-500">
                                    média {item.pontuacao_media_sem_oferta ?? '—'} pts (sem oferta)
                                  </p>
                                  <p className="text-xs text-slate-500">
                                    {item.total_aulas} registro(s) · {item.matriculados ?? '—'} matric.
                                  </p>
                                  {item.total_estudou != null && (
                                    <p className="text-xs text-teal-600">
                                      {item.total_estudou} lição(ões) estudada(s)
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </TabsContent>
                <TabsContent value="adulto" className="mt-0">
                  {rankingAdulto.length === 0 ? (
                    <div className="text-center py-12 text-slate-500">
                      <Trophy className="mx-auto h-12 w-12 text-slate-300 mb-2" />
                      <p>Nenhuma sala adulta no ranking deste período.</p>
                      <p className="text-sm">Cadastre salas como adulto ou registre chamadas.</p>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {rankingAdulto.map((item, index) => {
                        const maxTaxa = rankingAdulto[0]?.taxa_comparecimento || 1;
                        const taxa = Number(item.taxa_comparecimento ?? 0);
                        const percentual = maxTaxa > 0 ? (taxa / maxTaxa) * 100 : 0;
                        return (
                          <div
                            key={item.sala_id}
                            className={`p-3 sm:p-4 rounded-xl border ${
                              index === 0 ? 'bg-amber-50 border-amber-200' :
                              index === 1 ? 'bg-slate-50 border-slate-200' :
                              index === 2 ? 'bg-orange-50 border-orange-200' :
                              'bg-white border-slate-100'
                            }`}
                            data-testid={`ranking-item-adulto-${item.sala_id}`}
                          >
                            <div className="flex items-center gap-2 sm:gap-4">
                              <div className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 shrink-0">
                                {getRankingIcon(item.posicao)}
                              </div>
                              <div className="flex-1 min-w-0">
                                <div className="flex flex-wrap items-start justify-between gap-x-2 gap-y-1 mb-2">
                                  <div className="min-w-0">
                                    <span className="font-semibold text-slate-900 text-sm sm:text-base break-words">{item.sala_nome}</span>
                                    {item.faixa_etaria && (
                                      <span className="text-xs sm:text-sm text-slate-500 ml-1 sm:ml-2">({item.faixa_etaria})</span>
                                    )}
                                  </div>
                                  <div className="text-right shrink-0">
                                    <span className="text-lg sm:text-2xl font-bold text-slate-900 font-heading">
                                      {Math.round(taxa * 100)}
                                    </span>
                                    <span className="text-xs sm:text-sm text-slate-500 ml-1">% pres.</span>
                                  </div>
                                </div>
                                <Progress value={percentual} className="h-2" />
                                <div className="flex flex-wrap gap-x-3 gap-y-0.5 mt-1">
                                  <p className="text-xs text-slate-500">
                                    média {item.pontuacao_media_sem_oferta ?? '—'} pts (sem oferta)
                                  </p>
                                  <p className="text-xs text-slate-500">
                                    {item.total_aulas} registro(s) · {item.matriculados ?? '—'} matric.
                                  </p>
                                  {item.total_estudou != null && (
                                    <p className="text-xs text-teal-600">
                                      {item.total_estudou} lição(ões) estudada(s)
                                    </p>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </TabsContent>
                </div>
              </Tabs>

              {/* Pontuação Info */}
              <Card className="mt-6 bg-slate-50 border-slate-200">
                <CardContent className="p-4">
                  <h4 className="font-semibold text-slate-900 mb-2">Sistema de Pontuação</h4>
                  <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-6 gap-2 text-sm">
                    <div className="flex items-center gap-2">
                      <Badge className="bg-sky-100 text-sky-700">+10</Badge>
                      <span className="text-slate-600">Presença</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-emerald-100 text-emerald-700">+5</Badge>
                      <span className="text-slate-600">Bíblia</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-violet-100 text-violet-700">+5</Badge>
                      <span className="text-slate-600">Revista</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-teal-100 text-teal-700">+5</Badge>
                      <span className="text-slate-600">Estudou</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-amber-100 text-amber-700">+15</Badge>
                      <span className="text-slate-600">Visitante (por pessoa)</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <Badge className="bg-pink-100 text-pink-700">+1/R$</Badge>
                      <span className="text-slate-600">Oferta (total da sala / dia)</span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </CardContent>
          </Card>
        </TabsContent>

        {!isProfessor && (
          <TabsContent value="financeiro" className="mt-6 outline-none" data-testid="ebd-financeiro-content">
            <Card className="ebd-panel-card">
              <CardHeader className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                <div>
                  <CardTitle className="card-title flex items-center gap-2">
                    <Wallet className="h-5 w-5 text-[#2563EB]" />
                    Ofertas por sala
                  </CardTitle>
                  <CardDescription>
                    Totais registrados na chamada por período. Período:{' '}
                    {ofertaFinData
                      ? `${format(new Date(ofertaFinData.data_inicio + 'T12:00:00'), 'dd/MM/yyyy', { locale: ptBR })} — ${format(new Date(ofertaFinData.data_fim + 'T12:00:00'), 'dd/MM/yyyy', { locale: ptBR })}`
                      : '—'}
                  </CardDescription>
                </div>
                <div className="flex w-full flex-col gap-3 sm:w-auto sm:flex-row sm:items-end">
                  {ofertaFinPeriodo === 'dia' && (
                    <div className="w-full sm:w-44">
                      <Label className="text-xs text-[#64748B]">Data de referência</Label>
                      <Input
                        type="date"
                        value={ofertaFinDataRef}
                        onChange={(e) => setOfertaFinDataRef(e.target.value)}
                        className="input-field mt-1 h-10 rounded-lg"
                        data-testid="ebd-fin-data-ref"
                      />
                    </div>
                  )}
                  <div className="w-full sm:w-48">
                    <Label className="text-xs text-[#64748B]">Período</Label>
                    <Select value={ofertaFinPeriodo} onValueChange={setOfertaFinPeriodo}>
                      <SelectTrigger className="input-field mt-1 h-10 rounded-lg" data-testid="ebd-fin-periodo">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="dia">Dia</SelectItem>
                        <SelectItem value="mes">Mês</SelectItem>
                        <SelectItem value="trimestre">Trimestre</SelectItem>
                        <SelectItem value="semestre">Semestre</SelectItem>
                        <SelectItem value="ano">Ano</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </CardHeader>
              <CardContent className="space-y-6">
                {ofertaFinLoading && (
                  <div className="flex items-center justify-center gap-2 py-12 text-[#64748B]">
                    <Loader2 className="h-6 w-6 animate-spin text-[#2563EB]" />
                    <span>Carregando relatório…</span>
                  </div>
                )}
                {!ofertaFinLoading && ofertaFinData && (
                  <>
                    <div className="overflow-x-auto rounded-xl border border-[#E5E7EB]">
                      <Table>
                        <TableHeader>
                          <TableRow className="bg-[#F8FAFC] hover:bg-[#F8FAFC]">
                            <TableHead className="font-semibold text-[#0F172A]">Sala</TableHead>
                            <TableHead className="text-right font-semibold text-[#0F172A]">Total (R$)</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {(ofertaFinData.por_sala || []).map((row) => (
                            <TableRow key={row.sala_id}>
                              <TableCell className="font-medium text-[#0F172A]">{row.sala_nome}</TableCell>
                              <TableCell className="text-right tabular-nums text-[#64748B]">
                                {(Number(row.total) || 0).toLocaleString('pt-BR', {
                                  minimumFractionDigits: 2,
                                  maximumFractionDigits: 2,
                                })}
                              </TableCell>
                            </TableRow>
                          ))}
                          <TableRow className="bg-[#DBEAFE]/50 font-semibold hover:bg-[#DBEAFE]/50">
                            <TableCell className="text-[#0F172A]">Total geral</TableCell>
                            <TableCell className="text-right tabular-nums text-[#2563EB]">
                              {(Number(ofertaFinData.total_geral) || 0).toLocaleString('pt-BR', {
                                minimumFractionDigits: 2,
                                maximumFractionDigits: 2,
                              })}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </div>

                    <div>
                      <h3 className="mb-2 text-sm font-semibold text-[#0F172A]">Comparativo por sala</h3>
                      {(ofertaFinData.por_sala || []).some((r) => Number(r.total) > 0) ? (
                        <div className="h-72 w-full min-w-0 sm:h-80">
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                              data={(ofertaFinData.por_sala || []).map((s) => ({
                                nome:
                                  (s.sala_nome || '').length > 16
                                    ? `${(s.sala_nome || '').slice(0, 16)}…`
                                    : s.sala_nome || '—',
                                total: Number(s.total) || 0,
                              }))}
                              margin={{ top: 8, right: 8, left: 8, bottom: 48 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" className="stroke-[#E5E7EB]" />
                              <XAxis
                                dataKey="nome"
                                tick={{ fontSize: 11, fill: '#64748B' }}
                                interval={0}
                                angle={-28}
                                textAnchor="end"
                                height={60}
                              />
                              <YAxis
                                tick={{ fontSize: 11, fill: '#64748B' }}
                                tickFormatter={(v) =>
                                  Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 0 })
                                }
                              />
                              <Tooltip
                                formatter={(value) =>
                                  Number(value).toLocaleString('pt-BR', {
                                    style: 'currency',
                                    currency: 'BRL',
                                  })
                                }
                                contentStyle={{
                                  borderRadius: 8,
                                  border: '1px solid #E5E7EB',
                                }}
                              />
                              <Bar dataKey="total" fill="#2563EB" radius={[8, 8, 0, 0]} />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      ) : (
                        <p className="rounded-xl border border-dashed border-[#E5E7EB] py-8 text-center text-sm text-[#64748B]">
                          Nenhuma oferta registrada neste período.
                        </p>
                      )}
                    </div>

                    {(ofertaFinData.evolucao || []).length > 1 && (
                      <div>
                        <h3 className="mb-2 text-sm font-semibold text-[#0F172A]">Evolução no período</h3>
                        <div className="h-64 w-full min-w-0 sm:h-72">
                          <ResponsiveContainer width="100%" height="100%">
                            <LineChart
                              data={ofertaFinData.evolucao}
                              margin={{ top: 8, right: 8, left: 8, bottom: 8 }}
                            >
                              <CartesianGrid strokeDasharray="3 3" className="stroke-[#E5E7EB]" />
                              <XAxis dataKey="rotulo" tick={{ fontSize: 10, fill: '#64748B' }} interval="preserveStartEnd" />
                              <YAxis
                                tick={{ fontSize: 11, fill: '#64748B' }}
                                tickFormatter={(v) =>
                                  Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 0 })
                                }
                              />
                              <Tooltip
                                formatter={(value) =>
                                  Number(value).toLocaleString('pt-BR', {
                                    style: 'currency',
                                    currency: 'BRL',
                                  })
                                }
                                contentStyle={{
                                  borderRadius: 8,
                                  border: '1px solid #E5E7EB',
                                }}
                              />
                              <Line
                                type="monotone"
                                dataKey="total"
                                stroke="#2563EB"
                                strokeWidth={2}
                                dot={{ r: 3, fill: '#2563EB' }}
                              />
                            </LineChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Chamada Dialog */}
      <Dialog open={chamadaDialogOpen} onOpenChange={setChamadaDialogOpen}>
        <BottomSheetContent className="flex max-h-[92vh] flex-col overflow-hidden p-0 sm:max-h-[90vh] sm:max-w-4xl sm:overflow-y-auto sm:p-6">
          <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 pb-28 pt-2 sm:px-0 sm:pt-0 md:pb-6">
            <DialogHeader className="space-y-2 text-left">
              <DialogTitle className="text-xl font-semibold text-[#0F172A] sm:text-lg">
                Chamada - {selectedSala?.nome}
              </DialogTitle>
              <DialogDescription className="text-sm text-[#64748B]">
                Registre a presença e a participação dos alunos.
              </DialogDescription>
            </DialogHeader>

            <div className="mt-4 space-y-4">
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <div>
                  <Label className="text-sm font-medium text-[#0F172A]">Data da aula</Label>
                  <Input
                    type="date"
                    value={chamadaData.data_aula}
                    onChange={(e) => {
                      const d = e.target.value;
                      setChamadaData((prev) => ({ ...prev, data_aula: d }));
                      if (selectedSala) fetchAlunosSala(selectedSala.id, d);
                    }}
                    className="input-field mt-2 h-11 min-h-[44px] rounded-xl text-base"
                  />
                </div>
                <div>
                  <Label className="text-sm font-medium text-[#0F172A]">Lição estudada</Label>
                  <Input
                    value={chamadaData.licao_estudada}
                    onChange={(e) => setChamadaData({ ...chamadaData, licao_estudada: e.target.value })}
                    className="input-field mt-2 h-11 min-h-[44px] rounded-xl text-base"
                    placeholder="Ex.: Lição 1"
                  />
                </div>
              </div>

              <div className="rounded-xl border border-[#E5E7EB] bg-[#F8FAFC] p-4 shadow-sm">
                <Label htmlFor="oferta-sala-chamada-dialog" className="text-sm font-medium text-[#0F172A]">
                  Oferta total da sala (R$)
                </Label>
                <Input
                  id="oferta-sala-chamada-dialog"
                  type="number"
                  step="0.01"
                  min="0"
                  className="input-field mt-2 h-11 min-h-[44px] max-w-full rounded-xl text-base md:max-w-xs"
                  value={chamadaData.oferta_sala}
                  onChange={(e) => setChamadaData({ ...chamadaData, oferta_sala: e.target.value })}
                  placeholder="0"
                />
                <p className="mt-2 text-xs leading-relaxed text-[#64748B]">
                  Valor único para esta sala nesta data (+1 ponto por R$ 1,00 no total do dia).
                </p>
              </div>

              {chamadaData.presencas.length > 0 ? (
                <>
                  <div className="md:hidden">
                    <PresencaCards
                      presencas={chamadaData.presencas}
                      onUpdate={updatePresenca}
                      canGerirAlunos={canGerirAlunosEbd}
                      onOpenExcluirAluno={abrirExcluirAluno}
                      onOpenEditarAluno={abrirEditarAluno}
                    />
                  </div>
                  <div className="hidden overflow-x-auto md:block">
                    <Table>
                      <TableHeader>
                        <TableRow className="table-header">
                          <TableHead>Aluno</TableHead>
                          <TableHead className="text-center">Presente</TableHead>
                          <TableHead className="text-center">Bíblia</TableHead>
                          <TableHead className="text-center">Revista</TableHead>
                          <TableHead className="text-center">Estudou</TableHead>
                          <TableHead className="text-center">Visitantes</TableHead>
                          {canGerirAlunosEbd && (
                            <TableHead className="w-14 text-center font-normal text-slate-500">Ações</TableHead>
                          )}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {chamadaData.presencas.map((p) => (
                          <TableRow key={p.aluno_id}>
                            <TableCell className="font-medium">{p.nome}</TableCell>
                            <TableCell className="text-center">
                              <Checkbox
                                checked={p.presente}
                                onCheckedChange={(c) => updatePresenca(p.aluno_id, 'presente', c)}
                              />
                            </TableCell>
                            <TableCell className="text-center">
                              <Checkbox
                                checked={p.trouxe_biblia}
                                onCheckedChange={(c) => updatePresenca(p.aluno_id, 'trouxe_biblia', c)}
                              />
                            </TableCell>
                            <TableCell className="text-center">
                              <Checkbox
                                checked={p.trouxe_revista}
                                onCheckedChange={(c) => updatePresenca(p.aluno_id, 'trouxe_revista', c)}
                              />
                            </TableCell>
                            <TableCell className="text-center">
                              <Checkbox
                                checked={p.licao_estudada}
                                onCheckedChange={(c) => updatePresenca(p.aluno_id, 'licao_estudada', c)}
                              />
                            </TableCell>
                            <TableCell className="text-center">
                              <div className="flex justify-center">
                                <VisitanteNumeroControl
                                  value={p.visitante_numero}
                                  onChange={(n) => updatePresenca(p.aluno_id, 'visitante_numero', n)}
                                />
                              </div>
                            </TableCell>
                            {canGerirAlunosEbd && (
                              <TableCell className="text-center">
                                <div className="flex justify-center">
                                  <AlunoRowAcoes
                                    alunoId={p.aluno_id}
                                    nome={p.nome}
                                    canGerir={canGerirAlunosEbd}
                                    onEditar={abrirEditarAluno}
                                    onExcluir={abrirExcluirAluno}
                                  />
                                </div>
                              </TableCell>
                            )}
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </>
              ) : (
                <div className="py-8 text-center text-[#64748B] md:pb-8">
                  <Users className="mx-auto mb-2 h-12 w-12 text-[#CBD5E1]" />
                  <p className="text-sm font-medium text-[#0F172A]">Nenhum aluno cadastrado nesta sala.</p>
                  <p className="mt-1 text-sm">Você ainda pode salvar a oferta da sala.</p>
                </div>
              )}
            </div>

          </div>

          <div className="md:hidden shrink-0 border-t border-[#E5E7EB] bg-white px-4 py-3 shadow-[0_-4px_16px_rgba(15,23,42,0.08)] pb-[max(0.75rem,env(safe-area-inset-bottom))]">
            <Button
              type="button"
              className="h-12 w-full rounded-xl bg-[#2563EB] text-base font-semibold text-white shadow-sm hover:bg-[#1D4ED8] active:scale-[0.98] disabled:opacity-60"
              onClick={handleSalvarChamada}
              disabled={submitting}
            >
              {submitting ? (
                <Loader2 className="mr-2 h-5 w-5 animate-spin" />
              ) : (
                <Save className="mr-2 h-5 w-5" />
              )}
              Salvar chamada
            </Button>
          </div>

          <DialogFooter className="hidden border-t border-transparent px-6 pb-6 pt-2 md:flex md:border-0 md:px-0 md:pb-0 md:pt-0">
            <Button type="button" variant="outline" onClick={() => setChamadaDialogOpen(false)}>
              Cancelar
            </Button>
            <Button onClick={handleSalvarChamada} className="ebd-btn-primary" disabled={submitting}>
              {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Save className="mr-2 h-4 w-4" />}
              Salvar chamada
            </Button>
          </DialogFooter>
        </BottomSheetContent>
      </Dialog>

      <AlertDialog
        open={excluirSalaDialog.open}
        onOpenChange={(open) => {
          if (!open) {
            setExcluirSalaDialog({ open: false, sala: null });
            pendingExcluirSalaRef.current = { sala: null };
          }
        }}
      >
        <AlertDialogContent className="border-slate-200 sm:max-w-md sm:rounded-2xl shadow-xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-slate-900">Excluir sala?</AlertDialogTitle>
            <AlertDialogDescription className="text-left text-slate-600 leading-relaxed">
              A sala{' '}
              <span className="font-semibold text-slate-800">
                &quot;{excluirSalaDialog.sala?.nome}&quot;
              </span>{' '}
              será desativada e os alunos vinculados serão desativados na EBD. O histórico de presenças não é
              apagado.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-0">
            <AlertDialogCancel disabled={excluindoSala}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={excluindoSala}
              className="bg-red-600 text-white hover:bg-red-700 focus:ring-red-600"
              onClick={() => {
                void confirmarExcluirSala();
              }}
            >
              {excluindoSala ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Excluir sala'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <AlertDialog
        open={excluirAlunoDialog.open}
        onOpenChange={(open) => {
          if (!open) {
            setExcluirAlunoDialog({ open: false, alunoId: null, nome: '' });
            pendingExcluirAlunoRef.current = { alunoId: null, nome: '' };
          }
        }}
      >
        <AlertDialogContent className="border-slate-200 sm:max-w-md sm:rounded-2xl shadow-xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-slate-900">Remover aluno da EBD?</AlertDialogTitle>
            <AlertDialogDescription className="text-left text-slate-600 leading-relaxed">
              {excluirAlunoDialog.nome ? (
                <>
                  O cadastro de{' '}
                  <span className="font-semibold text-slate-800">
                    &quot;{excluirAlunoDialog.nome}&quot;
                  </span>{' '}
                  será desativado. As presenças já registradas permanecem no sistema.
                </>
              ) : (
                'O cadastro deste aluno será desativado. As presenças já registradas permanecem no sistema.'
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-0">
            <AlertDialogCancel disabled={excluindoAluno}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              disabled={excluindoAluno}
              className="bg-red-600 text-white hover:bg-red-700 focus:ring-red-600"
              onClick={() => {
                void confirmarExcluirAluno();
              }}
            >
              {excluindoAluno ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Remover aluno'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <Dialog
        open={editarAlunoDialog.open}
        onOpenChange={(open) => {
          if (!open) setEditarAlunoDialog({ open: false, alunoId: null, nome: '' });
        }}
      >
        <DialogContent className="border-slate-200 sm:max-w-md sm:rounded-2xl">
          <DialogHeader>
            <DialogTitle className="text-slate-900">Editar nome do aluno</DialogTitle>
            <DialogDescription>O nome aparece na chamada e nos relatórios desta igreja.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSalvarNomeAluno} className="space-y-4 pt-1">
            <div>
              <Label htmlFor="ebd-edit-aluno-nome">Nome completo</Label>
              <Input
                id="ebd-edit-aluno-nome"
                value={editarAlunoDialog.nome}
                onChange={(e) =>
                  setEditarAlunoDialog((prev) => ({ ...prev, nome: e.target.value }))
                }
                className="input-field mt-1"
                placeholder="Nome do aluno"
                autoComplete="name"
              />
            </div>
            <DialogFooter className="gap-2 sm:gap-0">
              <Button
                type="button"
                variant="outline"
                onClick={() => setEditarAlunoDialog({ open: false, alunoId: null, nome: '' })}
              >
                Cancelar
              </Button>
              <Button type="submit" className="ebd-btn-primary" disabled={submitting}>
                {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Salvar'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* FAB — mobile only */}
      <div className="fixed bottom-6 right-6 z-40 flex flex-col items-end gap-3 sm:hidden">
        {fabOpen && (
          <>
            <button
              type="button"
              className="flex items-center gap-2 rounded-full border border-[#E5E7EB] bg-white px-4 py-2 text-sm font-medium text-[#0F172A] shadow-lg transition-transform hover:bg-slate-50 active:scale-[0.98]"
              onClick={() => {
                setFabOpen(false);
                setSalaDialogOpen(true);
              }}
            >
              <BookOpen className="h-4 w-4 text-[#2563EB]" />
              Nova sala
            </button>
            <button
              type="button"
              className="flex items-center gap-2 rounded-full border border-[#E5E7EB] bg-white px-4 py-2 text-sm font-medium text-[#0F172A] shadow-lg transition-transform hover:bg-slate-50 active:scale-[0.98]"
              onClick={() => {
                setFabOpen(false);
                setAlunoDialogOpen(true);
              }}
            >
              <Users className="h-4 w-4 text-[#16A34A]" />
              Adicionar aluno
            </button>
          </>
        )}
        <button
          type="button"
          className="flex h-14 w-14 items-center justify-center rounded-full bg-[#2563EB] text-white shadow-lg transition-transform hover:bg-[#1D4ED8] active:scale-[0.98]"
          onClick={() => setFabOpen((v) => !v)}
          aria-label="Ações"
        >
          {fabOpen ? <X className="h-6 w-6" /> : <Plus className="h-6 w-6" />}
        </button>
      </div>
    </div>
  );
};

export default EBD;
